import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
    ArrowLeft,
    ChevronLeft,
    ChevronRight,
    Edit,
    Phone,
    Mail,
    MapPin,
    Calendar,
    User,
    Building2,
    Tag,
    FileText,
    CalendarPlus
} from "lucide-react";
import { toast } from "sonner";
import { getLeadDataById } from "../services/crmServices/leadService";
import { selectFilteredLeads, selectCurrentLeadIndex, setCurrentLeadIndex } from "../store/slices/leadsSlice";
import { getFullName } from "../services/common/util";
import { format } from "date-fns";
import { VoiceCallModal } from "../components/leads/VoiceCallModal";
import { selectLeadStatuses } from "../store/slices/leadStatusSlice";
import { useAuth } from "../hooks/useAuth";
import { ScheduleCallbackModal } from "../components/calendar/ScheduleCallbackModal";

export function ViewLead() {
    const { user } = useAuth();
    const leadStatuses = useSelector(selectLeadStatuses);
    const { _id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const filteredLeads = useSelector(selectFilteredLeads);
    const currentLeadIndex = useSelector(selectCurrentLeadIndex);

    const [lead, setLead] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showCallModal, setShowCallModal] = useState(false);
    const [showCallbackModal, setShowCallbackModal] = useState(false);

    useEffect(() => {
        if (!user.permissions.leads.view) {
            navigate("/leads");
        }
        if (_id) {
            fetchLeadData(_id);
            // Find and set the current lead index in filtered leads
            const leadIndex = filteredLeads.findIndex((lead) => lead._id === _id);
            if (leadIndex !== -1) {
                dispatch(setCurrentLeadIndex(leadIndex));
            }
        }
    }, [_id, filteredLeads, dispatch]);

    const fetchLeadData = async (leadId) => {
        setLoading(true);
        try {
            const response = await getLeadDataById(leadId);
            // Check for access denied (403)
            if (response?.status === 403) {
                toast.error(response.message || "You no longer have access to this lead. It may have been reassigned.");
                navigate("/leads");
                return;
            }
            if (response?.data?._id) {
                setLead(response.data);
            } else if (response?.status === 404) {
                toast.error("Lead not found");
                navigate("/leads");
            } else {
                toast.error("Lead not found");
                navigate("/leads");
            }
        } catch (error) {
            console.error("Error fetching lead:", error);
            // Handle 403 access denied errors
            if (error.response?.status === 403) {
                toast.error(error.response?.data?.message || "You no longer have access to this lead. It may have been reassigned.");
            } else if (error.response?.status === 404) {
                toast.error("Lead not found");
            } else {
                toast.error("Failed to fetch lead data");
            }
            navigate("/leads");
        } finally {
            setLoading(false);
        }
    };

    const handlePrevious = () => {
        if (currentLeadIndex > 0) {
            const previousLead = filteredLeads[currentLeadIndex - 1];
            navigate(`/leads/view/${previousLead._id}`);
        }
    };

    const handleNext = () => {
        if (currentLeadIndex < filteredLeads.length - 1) {
            const nextLead = filteredLeads[currentLeadIndex + 1];
            navigate(`/leads/view/${nextLead._id}`);
        }
    };

    const handleEdit = () => {
        navigate(`/leads/edit/${_id}`);
    };

    const handleBack = () => {
        navigate("/leads");
    };

    const handleCallLead = () => {
        if (!lead?.phone) {
            toast.error("No phone number available for this lead");
            return;
        }

        if (
            !(user.commpeak && user.permissions.voip?.commpeak) &&
            !(user.voiso && user.permissions.voip?.voiso) &&
            !(user.coperato && user.permissions.voip?.coperato) &&
            !(user.squaretalk && user.permissions.voip?.squaretalk) &&
            !(user.octella && user.permissions.voip?.octella) &&
            !(user.ccc && user.permissions.voip?.ccc)
        ) {
            toast.error("No call provider configured. Please contact administrator to set up VoIP.");
            return;
        }

        setShowCallModal(true);
    };

    const handleSendEmail = () => {
        if (lead?.email) {
            // Create mailto link
            const mailtoLink = `mailto:${lead.email}?subject=Follow up - ${lead.firstName} ${lead.lastName || ""}`;
            window.open(mailtoLink, "_blank");
        } else {
            toast.error("No email address available for this lead");
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "new":
                return "bg-blue-100 text-blue-800";
            case "interested":
                return "bg-green-100 text-green-800";
            case "follow_up":
                return "bg-yellow-100 text-yellow-800";
            case "converted":
                return "bg-purple-100 text-purple-800";
            case "not_answered":
                return "bg-gray-100 text-gray-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    const getCallButtonText = () => {
        const providers = [];
        if (user.commpeak && user.permissions.voip?.commpeak) providers.push("CommPeak");
        if (user.voiso && user.permissions.voip?.voiso) providers.push("Voiso");
        if (user.coperato && user.permissions.voip?.coperato) providers.push("Coperato");
        if (user.squaretalk && user.permissions.voip?.squaretalk) providers.push("SquareTalk");
        if (user.octella && user.permissions.voip?.octella) providers.push("Octella");
        if (user.ccc && user.permissions.voip?.ccc) providers.push("Call Center Connect");
        
        if (providers.length > 1) {
            return `Call Lead (${providers.join("/")})`;
        } else if (providers.length === 1) {
            return `Call Lead (${providers[0]})`;
        } else {
            return "Call Lead (Not configured)";
        }
    };

    if (loading) {
        return (
            <div className="container mx-auto px-4 sm:px-6">
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    if (!lead) {
        return (
            <div className="container mx-auto px-4 sm:px-6">
                <div className="text-center py-12">
                    <h3 className="text-lg font-medium text-gray-900 mb-1">Lead not found</h3>
                    <p className="text-gray-500">The lead you&apos;re looking for doesn&apos;t exist.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 sm:px-6">
            {/* Header with navigation */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4">
                        <h1 className="text-2xl font-bold text-gray-900">
                            {lead.firstName} {lead.lastName || ""}
                        </h1>
                    </div>
                    <Button onClick={handleEdit} className="bg-blue-600 hover:bg-blue-700">
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Lead
                    </Button>
                </div>

                {/* Navigation controls */}
                {filteredLeads.length > 1 && (
                    <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                        <Button
                            variant="outline"
                            onClick={handlePrevious}
                            disabled={currentLeadIndex <= 0}
                            className="flex items-center"
                        >
                            <ChevronLeft className="h-4 w-4 mr-2" />
                            Previous
                        </Button>

                        <span className="text-sm text-gray-600">
                            {currentLeadIndex + 1} of {filteredLeads.length} leads
                        </span>

                        <Button
                            variant="outline"
                            onClick={handleNext}
                            disabled={currentLeadIndex >= filteredLeads.length - 1}
                            className="flex items-center"
                        >
                            Next
                            <ChevronRight className="h-4 w-4 ml-2" />
                        </Button>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Information */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Basic Information */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center">
                                <User className="h-5 w-5 mr-2" />
                                Basic Information
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-600">First Name</label>
                                    <p className="text-sm text-gray-900">{lead.firstName || "-"}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-600">Last Name</label>
                                    <p className="text-sm text-gray-900">{lead.lastName || "-"}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-600">User Name</label>
                                    <p className="text-sm text-gray-900">{lead.userName || "-"}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-600">Email</label>
                                    <div className="flex items-center space-x-2">
                                        <Mail className="h-4 w-4 text-gray-400" />
                                        <p className="text-sm text-gray-900">{lead.email}</p>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-600">Phone</label>
                                    <div className="flex items-center space-x-2">
                                        <Phone className="h-4 w-4 text-gray-400" />
                                        <p className="text-sm text-gray-900">{lead.phone || "-"}</p>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-600">Second Phone</label>
                                    <div className="flex items-center space-x-2">
                                        <Phone className="h-4 w-4 text-gray-400" />
                                        <p className="text-sm text-gray-900">{lead.secondPhone || "-"}</p>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Location Information */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center">
                                <MapPin className="h-5 w-5 mr-2" />
                                Location & Status
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-600">Country</label>
                                    <p className="text-sm text-gray-900">{lead.country || "-"}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-600">City</label>
                                    <p className="text-sm text-gray-900">{lead.city || "-"}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-600">Address</label>
                                    <p className="text-sm text-gray-900">{lead.address || "-"}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-600">Status</label>
                                    <Badge variant="secondary" className={getStatusColor(lead.status)}>
                                        {leadStatuses.find((option) => option.value === lead.status)?.label ||
                                            lead.status}
                                    </Badge>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Assignment Information */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center">
                                <Building2 className="h-5 w-5 mr-2" />
                                Assignment & Source
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-600">Desk</label>
                                    <p className="text-sm text-gray-900">{lead.desk?.name || "-"}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-600">Manager</label>
                                    <p className="text-sm text-gray-900">
                                        {lead.desk?.manager ? getFullName(lead.desk.manager) : "-"}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-600">Agent</label>
                                    <p className="text-sm text-gray-900">
                                        {lead.agent ? getFullName(lead.agent) : "-"}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-600">Source</label>
                                    <div className="flex items-center space-x-2">
                                        <Tag className="h-4 w-4 text-gray-400" />
                                        <p className="text-sm text-gray-900">{lead.source || "-"}</p>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-600">Affiliate</label>
                                    <p className="text-sm text-gray-900">{lead.affiliate || "-"}</p>
                                </div>
                                {lead.balance && (
                                    <div className="md:col-span-2">
                                        <label className="text-sm font-medium text-gray-600">Balance (Amount in Bank)</label>
                                        <p className="text-sm font-semibold text-green-700 bg-green-50 px-3 py-2 rounded border border-green-200">
                                            {lead.balanceCurrency || 'USD'} {lead.balance}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Notes */}
                    {lead.extra && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center">
                                    <FileText className="h-5 w-5 mr-2" />
                                    Notes
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm text-gray-900 whitespace-pre-wrap">{lead.extra}</p>
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Dates */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center">
                                <Calendar className="h-5 w-5 mr-2" />
                                Important Dates
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <div>
                                <label className="text-sm font-medium text-gray-600">Registered</label>
                                <p className="text-sm text-gray-900">
                                    {lead.createdAt ? format(new Date(lead.createdAt), "MMM dd, yyyy HH:mm") : "-"}
                                </p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">Last Modified</label>
                                <p className="text-sm text-gray-900">
                                    {lead.updatedAt ? format(new Date(lead.updatedAt), "MMM dd, yyyy HH:mm") : "-"}
                                </p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">Last Communication</label>
                                <p className="text-sm text-gray-900">
                                    {lead.lastCommDate ? format(new Date(lead.lastCommDate), "MMM dd, yyyy") : "-"}
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Last Comment */}
                    {lead.lastComment && (
                        <Card>
                            <CardHeader>
                                <CardTitle>Last Comment</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-sm text-gray-900 whitespace-pre-wrap">{lead.lastComment}</p>
                            </CardContent>
                        </Card>
                    )}

                    {/* Quick Actions */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Quick Actions</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <Button onClick={handleEdit} className="w-full bg-blue-600 hover:bg-blue-700">
                                <Edit className="h-4 w-4 mr-2" />
                                Edit Lead
                            </Button>
                            {lead.phone && (
                                <Button
                                    onClick={handleCallLead}
                                    variant="outline"
                                    className="w-full text-green-600 hover:text-green-700 border-green-600 hover:border-green-700"
                                    disabled={!(user.commpeak && user.permissions.voip?.commpeak) && !(user.voiso && user.permissions.voip?.voiso) && !(user.coperato && user.permissions.voip?.coperato) && !(user.squaretalk && user.permissions.voip?.squaretalk)}
                                >
                                    <Phone className="h-4 w-4 mr-2" />
                                    {getCallButtonText()}
                                </Button>
                            )}
                            <Button
                                onClick={handleSendEmail}
                                variant="outline"
                                className="w-full text-blue-600 hover:text-blue-700 border-blue-600 hover:border-blue-700"
                            >
                                <Mail className="h-4 w-4 mr-2" />
                                Send Email
                            </Button>
                            <Button
                                onClick={() => setShowCallbackModal(true)}
                                variant="outline"
                                className="w-full text-purple-600 hover:text-purple-700 border-purple-600 hover:border-purple-700"
                            >
                                <CalendarPlus className="h-4 w-4 mr-2" />
                                Schedule Callback
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Voice Call Modal */}
            <VoiceCallModal open={showCallModal} onClose={() => setShowCallModal(false)} lead={lead} />
            <ScheduleCallbackModal
                open={showCallbackModal}
                onClose={() => setShowCallbackModal(false)}
                lead={lead}
            />
        </div>
    );
}
